/**
 * Custom element for handling product matching and variant switching.
 * Automatically detects sections with data-section-to-replace attributes.
 *
 * @class ProductMatching
 * @extends HTMLElement
 */
class ProductMatching extends HTMLElement {
	constructor() {
		super();
		this.sectionId = this.dataset.sectionId;
		this.additionalSections = this.dataset.additionalSections;
		this.forceRedirection = this.dataset.forceRedirection === 'true' || false;
		this.init();
	}

	init() {
		this.addEventListener('click', this.handleClick.bind(this), true);
	}

	/**
	 * Scan DOM for sections with data-section-to-replace attribute
	 * @returns {string[]} Array of section IDs to replace
	 */
	getSectionsToReplace() {
		const sectionsToReplace = [];
		const elementsWithReplaceAttribute = document.querySelectorAll('[data-section-to-replace]');

		elementsWithReplaceAttribute.forEach((element) => {
			const sectionId = element.dataset.sectionToReplace;
			if (sectionId && !sectionsToReplace.includes(sectionId)) {
				sectionsToReplace.push(sectionId);
			}
		});

		return sectionsToReplace;
	}

	/**
	 * Get all sections to update (including additional and auto-detected ones)
	 * @returns {string} Comma-separated list of section IDs
	 */
	getAllSectionsToUpdate() {
		const sections = [];

		// Add additional sections from data attribute (legacy)
		if (this.additionalSections) {
			sections.push(...this.additionalSections.split(',').map((s) => s.trim()));
		}

		// Add auto-detected sections with data-section-to-replace
		const sectionsToReplace = this.getSectionsToReplace();
		sections.push(...sectionsToReplace);

		// Remove duplicates and empty values
		return [...new Set(sections.filter((s) => s !== ''))];
	}

	/**
	 * Load product content
	 * @param {string} productUrl - URL of the product to load
	 */
	loadProduct(productUrl) {
		if (this.forceRedirection) {
			window.location.href = productUrl;
			return;
		}

		const sectionsToUpdate = this.getAllSectionsToUpdate();
		
		// If no sections to update, redirect to product page
		if (sectionsToUpdate.length === 0) {
			window.location.href = productUrl;
			return;
		}

		// Load the full page
		fetch(productUrl)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, 'text/html');
				
				// Update page title
				const newTitle = html.querySelector('head title');
				if (newTitle) {
					document.querySelector('head title').innerHTML = newTitle.innerHTML;
				}

				// Update URL
				window.history.pushState({}, '', productUrl);

				// Update main product info
				const newProductInfo = html.querySelector('product-info');
				const currentProductInfo = document.querySelector('product-info');
				
				if (newProductInfo && currentProductInfo) {
					HTMLUpdateUtility.viewTransition(
						currentProductInfo,
						newProductInfo,
						[(newContent) => {
							newContent.querySelectorAll('.scroll-trigger').forEach((element) => 
								element.classList.add('scroll-trigger--cancel')
							);
						}],
						[(newNode) => {
							window?.Shopify?.PaymentButton?.init();
							window?.ProductModel?.loadShopifyXR();
						}]
					);
				}

				// Update additional sections
				sectionsToUpdate.forEach((sectionId) => {
					const currentSection = document.querySelector(`[data-section-to-replace="${sectionId}"]`);
					const newSection = html.querySelector(`[data-section-to-replace="${sectionId}"]`);
					
					if (currentSection && newSection) {
						currentSection.innerHTML = newSection.innerHTML;
					}
				});

				// Scroll to top
				window.scrollTo({ top: 0, behavior: 'smooth' });
			})
			.catch((error) => {
				console.error('Error loading product:', error);
				// Fallback to full page reload
				window.location.href = productUrl;
			});
	}

	handleClick(event) {
		const matchingItem = event.target.closest('.product__matching-item.matching--other');
		if (!matchingItem) return;

		const productUrl = matchingItem.dataset.productTarget;
		if (!productUrl) return;

		event.preventDefault();
		event.stopPropagation();
		event.stopImmediatePropagation();

		this.loadProduct(productUrl);

		return false;
	}
}

customElements.get('product-matching') ||
	customElements.define('product-matching', ProductMatching);

